"use client";

import { useEffect, useState } from "react";
import { DotsIcon } from "@/components/icons/dots";
import { spacing, typography } from "@/styles/design-tokens";

type CountdownProps = {
  countdownDate: string;
  countdownText?: string;
  countdownPreText?: string;
};

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
};

const calculateTimeLeft = (date: string): TimeLeft => {
  const targetTime = new Date(date).getTime();
  if (Number.isNaN(targetTime)) return { days: 0, hours: 0, minutes: 0 };

  const now = Date.now();
  const diff = targetTime - now;

  if (diff <= 0) return { days: 0, hours: 0, minutes: 0 };

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
  };
};

export default function Countdown({
  countdownPreText,
  countdownText,
  countdownDate,
}: CountdownProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    calculateTimeLeft(countdownDate),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(countdownDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [countdownDate]);

  if (!countdownDate || Number.isNaN(new Date(countdownDate).getTime()))
    return null;

  const dotContainerClass = "space-y-1 md:space-y-2";
  const dotClass =
    "text-shadow-lg bg-black w-[10px] rounded-full aspect-square sm:w-4 lg:w-5";

  return (
    <section className={`${spacing.section} relative md:px-10 lg:px-16`}>
      <div
        className={`${spacing.inner} relative overflow-hidden shadow-[8px_12px_0_0_rgba(0,0,0,0.25)]`}
      >
        {countdownPreText && (
          <h3 className={`${typography.h5} w-full text-center z-10 relative`}>
            {countdownPreText}
          </h3>
        )}

        <div className="space-y-3 z-10 relative">
          {/* Timer digits */}
          <div aria-hidden="true" className="flex items-center justify-center">
            <time
              dateTime={`${timeLeft.days}D`}
              className={`${typography.h2} w-full text-center`}
            >
              {timeLeft.days}
            </time>
            <div className={dotContainerClass} aria-hidden="true">
              <div className={dotClass} />
              <div className={dotClass} />
            </div>

            <time
              dateTime={`${timeLeft.hours}H`}
              className={`${typography.h2} w-full text-center`}
            >
              {timeLeft.hours}
            </time>
            <div className={dotContainerClass} aria-hidden="true">
              <div className={dotClass} />
              <div className={dotClass} />
            </div>

            <time
              dateTime={`${timeLeft.minutes}M`}
              className={`${typography.h2} w-full text-center`}
            >
              {timeLeft.minutes}
            </time>
          </div>

          {/* Labels */}
          <div className="w-full flex items-center mt-2 gap-[10px] sm:gap-4 lg:gap-5 justify-center !mb-0">
            <h6 className={`${typography.captionLarge} w-full text-center`}>
              Days
            </h6>
            <h6 className={`${typography.captionLarge} w-full text-center`}>
              Hours
            </h6>
            <h6 className={`${typography.captionLarge} w-full text-center`}>
              Minutes
            </h6>
          </div>
        </div>

        {countdownText && (
          <h4
            className={`${typography.h6} w-full text-center z-10 relative !mb-0 mt-10 md:mt-12`}
          >
            {countdownText}
          </h4>
        )}
        <DotsIcon
          classes="absolute -top-[500px] -left-36 hidden xl:block"
          color="#fdc42d"
        />
        <DotsIcon
          classes="absolute rotate-180 -bottom-[500px] -right-36 opacity-40 xl:opacity-100"
          color="#fdc42d"
        />
      </div>
    </section>
  );
}
